import { useMemo } from "react";

import { EntityView } from "api/generated/model";
import { useEntitiesQuery } from "org/entities/queries/useEntitiesQuery";
import { AssignmentGroupId } from "utils/controls/assignmentUtils";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";

/**
 * Entity-level edit permissions for the assignment section of the control form.
 *
 * Determines which entities the current user can edit, based on the
 * actions returned by the entities query.
 *
 * Returns:
 *  - canEditAnyLinkedEntity: whether the user can edit at least one entity
 *    linked to the control program. If this is false, the assignment form is
 *    read-only. Already assigned entities are always a subset of linked
 *    entities, so this covers both. It is also what allows a user without edit
 *    access on the control itself to submit the form, as the backend accepts
 *    assignment-only updates on the entities they can edit.
 *  - canEditEntityInRow: for current assignment group rows, whether the user
 *    can edit the entity assigned to a specific assignment group
 *    (true if no entity is assigned).
 */
export function useEntityEditPermissions({
    entityIds,
    linkedEntities,
}: {
    entityIds: Record<AssignmentGroupId, number>;
    linkedEntities: EntityView[];
}) {
    const controlEntitiesEnabled = useFeatureFlag("controlEntities");
    const { data: entities } = useEntitiesQuery({
        all: false,
        scope: "control",
    });

    const editableEntityIds = useMemo(
        () =>
            new Set(
                entities.content
                    .filter((item) => item.actions.control.editAssignment)
                    .map((item) => item.resource.id)
            ),
        [entities]
    );

    const canEditAnyLinkedEntity =
        controlEntitiesEnabled &&
        linkedEntities.some((e) => editableEntityIds.has(e.id));

    const canEditEntityInRow = (
        assignmentGroupId: AssignmentGroupId
    ): boolean => {
        const entityId = entityIds[assignmentGroupId];
        if (!entityId) {
            return true;
        }
        return editableEntityIds.has(entityId);
    };

    return { canEditAnyLinkedEntity, canEditEntityInRow };
}
